

(() => {
  if (customElements.get('yumi-glass-tabs')) return;
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const spring = (value, stiffness, damping, mass = 1) => ({ value, target: value, velocity: 0, stiffness, damping, mass });

  // Exact damped-spring integration; time based, including on 120 Hz displays.
  function advance(s, dt) {
    const x = s.value - s.target;
    const a = s.damping / (2 * s.mass);
    const frequency = s.stiffness / s.mass - a * a;
    const decay = Math.exp(-a * dt);
    let position, velocity;
    if (Math.abs(frequency) < 0.00001) {
      const b = s.velocity + a * x;
      position = decay * (x + b * dt);
      velocity = decay * (s.velocity - a * b * dt);
    } else if (frequency > 0) {
      const w = Math.sqrt(frequency), sin = Math.sin(w * dt), cos = Math.cos(w * dt);
      const b = (s.velocity + a * x) / w;
      position = decay * (x * cos + b * sin);
      velocity = decay * ((b * w - a * x) * cos - (x * w + a * b) * sin);
    } else {
      const w = Math.sqrt(-frequency), r1 = -a + w, r2 = -a - w;
      const b = (s.velocity - r2 * x) / (r1 - r2), c = x - b;
      position = b * Math.exp(r1 * dt) + c * Math.exp(r2 * dt);
      velocity = b * r1 * Math.exp(r1 * dt) + c * r2 * Math.exp(r2 * dt);
    }
    s.value = s.target + position;
    s.velocity = velocity;
    const unit = s.stiffness < 300 || s.mass < 1 ? 0.0001 : 0.01;
    if (Math.abs(position) < unit && Math.abs(velocity) < unit * 10) {
      s.value = s.target; s.velocity = 0; return false;
    }
    return true;
  }

  customElements.define('yumi-glass-tabs', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.scroller = this.parentElement;
      this.lens = this.querySelector('.yumi-pill-nav__lens');
      if (!this.lens) {
        this.lens = document.createElement('span');
        this.lens.className = 'yumi-pill-nav__lens';
        this.lens.setAttribute('aria-hidden', 'true');
        this.lens.innerHTML = '<span class="yumi-pill-nav__reflection"></span><span class="yumi-pill-nav__travel-light"></span>';
        this.prepend(this.lens);
      }
      this.glow = this.querySelector('.yumi-pill-nav__glow');
      if (!this.glow) {
        this.glow = document.createElement('span');
        this.glow.className = 'yumi-pill-nav__glow';
        this.glow.setAttribute('aria-hidden', 'true');
        this.prepend(this.glow);
      }
      this.state = {
        left: spring(0, 420, 34), right: spring(0, 420, 34),
        top: spring(0, 500, 40), height: spring(34, 500, 40),
        press: spring(0, 520, 34, 0.6), lift: spring(0, 260, 24, 0.7),
        travel: spring(0, 220, 22, 0.6), hover: spring(0, 125, 25, 0.65),
        pointer: spring(0, 250, 29, 0.45)
      };
      const options = { signal: this.controller.signal };
      for (const [name, handler] of Object.entries({ pointerdown: this.pointerDown, pointermove: this.pointerMove, pointerup: this.pointerUp, pointercancel: this.pointerCancel, lostpointercapture: this.pointerCancel, pointerenter: this.pointerEnter, pointerleave: this.pointerLeave, keydown: this.keyDown })) {
        this.addEventListener(name, handler, options);
      }
      this.addEventListener('click', this.click, { ...options, capture: true });
      this.reduced.addEventListener('change', this.motionPreference, options);
      this.observer = new MutationObserver(() => this.scheduleSelection());
      this.observer.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-pressed', 'aria-current'] });
      this.resizeObserver = new ResizeObserver(() => this.scheduleSelection(true));
      this.resizeObserver.observe(this);
      for (const pill of this.pills()) this.resizeObserver.observe(pill);
      this.sync(true);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame); cancelAnimationFrame(this.selectionFrame);
      this.frame = this.selectionFrame = 0; this.drag = this.navigation = null;
      this.removeAttribute('data-glass-moving'); this.removeAttribute('data-glass-dragging');
      this.lastMode = null; this.suppressUntil = 0;
      this.initialized = false;
    }
    pills() { return [...this.querySelectorAll('a.yumi-pill-nav__pill, button.yumi-pill-nav__pill:not(:disabled)')]; }
    selected() { return this.querySelector('[aria-pressed="true"], [aria-current="page"]'); }
    geometry(pill) { return { left: pill.offsetLeft, top: pill.offsetTop, width: pill.offsetWidth, height: pill.offsetHeight }; }
    scheduleSelection(jump = false) {
      this.jumpRequested ||= jump;
      if (this.selectionFrame) return;
      this.selectionFrame = requestAnimationFrame(() => {
        this.selectionFrame = 0; this.sync(this.jumpRequested); this.jumpRequested = false;
      });
    }
    sync(jump = false) {
      const selected = this.selected();
      this.lens.hidden = this.glow.hidden = !selected;
      this.toggleAttribute('data-glass-ready', Boolean(selected));
      if (!selected || this.drag?.moved || this.navigation) return;
      const rect = this.geometry(selected);
      this.place(rect, jump || !this.initialized ? 'jump' : 'animate');
      this.initialized = true;
    }
    place(rect, mode) {
      const state = this.state;
      const target = { left: rect.left, right: rect.left + rect.width, top: rect.top, height: rect.height };
      // A completed collection fetch writes the same ARIA state again. Keep the
      // current spring and its velocity instead of restarting its settling phase.
      if (mode !== 'jump' && mode === this.lastMode && Object.keys(target).every(key => state[key].target === target[key])) return;
      this.lastMode = mode;
      this.targetWidth = rect.width;
      const rightward = rect.left + rect.width / 2 >= (state.left.value + state.right.value) / 2;
      const q = mode === 'drag' ? 0.45 * 0.35 : 0.45;
      for (const [name, leading] of [['left', !rightward], ['right', rightward]]) {
        const factor = leading ? 1 + 0.9 * q : 1 - 0.48 * q;
        state[name].stiffness = (mode === 'drag' ? 1100 : 420) * factor;
        state[name].damping = (mode === 'drag' ? 62 : 34) * Math.sqrt(factor);
      }
      let changed = false;
      for (const key of Object.keys(target)) {
        changed ||= state[key].target !== target[key];
        state[key].target = target[key];
        if (mode === 'jump' || this.reduced.matches) { state[key].value = target[key]; state[key].velocity = 0; }
      }
      if (mode === 'jump' || this.reduced.matches) this.render();
      else if (changed || mode === 'animate') this.wake();
    }
    wake() {
      if (this.frame) return;
      this.lastTime = performance.now();
      this.frame = requestAnimationFrame(this.tick);
    }
    tick = time => {
      this.frame = -1;
      const now = performance.now();
      const dt = Math.max(0, (now - this.lastTime) / 1000);
      this.lastTime = now;
      const scrolling = this.autoScroll(dt);
      this.state.travel.target = clamp(Math.abs((this.state.left.velocity + this.state.right.velocity) / 2) / 1400, 0, 1);
      let moving = false;
      for (const channel of Object.values(this.state)) {
        if (this.reduced.matches) { channel.value = channel.target; channel.velocity = 0; }
        else moving = advance(channel, dt) || moving;
      }
      this.render();
      this.toggleAttribute('data-glass-moving', moving || scrolling);
      if (moving || scrolling) this.frame = requestAnimationFrame(this.tick);
      else {
        this.frame = 0;
        if (this.navigation) { const href = this.navigation; this.navigation = null; location.assign(href); }
      }
    };
    render() {
      const s = this.state, width = Math.max(1, s.right.value - s.left.value), height = s.height.value;
      const stretch = clamp((width - this.targetWidth) / Math.max(1, this.targetWidth), -0.5, 1);
      const scaleY = clamp(1 - 0.16 * 0.45 * stretch, 0.8, 1.06);
      const scale = this.reduced.matches ? 1 : 1 - 0.02 * s.press.value + 0.04 * s.lift.value;
      Object.assign(this.lens.style, {
        width: `${width}px`, height: `${height}px`,
        transform: `translate(${s.left.value}px, ${s.top.value}px) scale(${scale}, ${scale * (this.reduced.matches ? 1 : scaleY)})`,
        '--ypn-reflection-x': `${-30 + s.hover.value * 36 + s.pointer.value * s.hover.value * 9}%`,
        '--ypn-reflection-opacity': String(0.16 + 0.18 * s.hover.value + 0.08 * Math.sin(Math.PI * s.hover.value)),
        '--ypn-travel-opacity': String(0.75 * s.travel.value),
        '--ypn-travel-x': `${(s.left.velocity + s.right.velocity >= 0 ? -0.6 : 0.6) * width}px`
      });
      Object.assign(this.glow.style, {
        opacity: String(this.reduced.matches ? 0 : clamp(0.45 * s.travel.value + 0.25 * s.lift.value, 0, 1)),
        transform: `translate(${s.left.value + width * 0.08}px, ${s.top.value + height * (0.34 + 0.16 * s.lift.value)}px) scale(${width * 0.84 / 100}, ${height * 0.9 / 40})`
      });
    }
    motionPreference = () => { if (!this.drag) this.sync(true); this.wake(); };
    pointerEnter = event => {
      if (event.pointerType !== 'mouse' || this.reduced.matches) return;
      this.state.hover.target = 1; this.wake();
    };
    pointerLeave = () => { this.state.hover.target = 0; this.wake(); };
    pointerDown = event => {
      if (!event.isPrimary || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const pill = event.target.closest('.yumi-pill-nav__pill');
      if (!pill || !this.contains(pill) || !this.initialized) return;
      this.suppressUntil = 0; this.navigation = null;
      this.rects = this.pills().map(p => ({ ...this.geometry(p), element: p })).sort((a, b) => a.left - b.left);
      const bounds = this.getBoundingClientRect();
      this.viewport = this.scroller.getBoundingClientRect();
      const x = event.clientX - bounds.left;
      const center = (this.state.left.value + this.state.right.value) / 2;
      this.state.press.target = 1;
      this.drag = {
        id: event.pointerId, x, clientX: event.clientX, clientY: event.clientY,
        startY: event.clientY, left: bounds.left, scroll: this.scroller.scrollLeft,
        offset: center - x, wanted: center, origin: this.selected(), moved: false,
        // Wrapped layouts retain native page scrolling and click/keyboard activation.
        canDrag: this.rects.length > 1 && this.rects.every(r => r.top === this.rects[0].top)
      };
      this.wake();
    };
    pointerMove = event => {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) {
        if (event.pointerType === 'mouse' && this.matches(':hover') && !this.reduced.matches) {
          const bounds = this.getBoundingClientRect();
          this.state.pointer.target = clamp((event.clientX - bounds.left) / bounds.width * 2 - 1, -1, 1);
          this.wake();
        }
        return;
      }
      drag.clientX = event.clientX;
      const x = event.clientX - drag.left + this.scroller.scrollLeft - drag.scroll;
      if (!drag.moved) {
        const dx = Math.abs(x - drag.x), dy = Math.abs(event.clientY - drag.startY);
        if (dy > 6 && dy > dx) { this.finish(event, true); return; }
        if (dx < 6 || !drag.canDrag) return;
        drag.moved = true; this.state.lift.target = 1;
        this.setAttribute('data-glass-dragging', '');
        try { this.setPointerCapture(event.pointerId); } catch {}
      }
      drag.wanted = drag.offset + x;
      this.place(this.rectAt(drag.wanted), 'drag');
    };
    rectAt(center) {
      const first = this.rects[0], last = this.rects[this.rects.length - 1];
      const c = clamp(center, first.left + first.width / 2, last.left + last.width / 2);
      let a = first, b = first;
      for (let i = 0; i < this.rects.length - 1; i++) {
        a = this.rects[i]; b = this.rects[i + 1];
        if (c <= b.left + b.width / 2) break;
      }
      const start = a.left + a.width / 2, end = b.left + b.width / 2;
      const t = end > start ? clamp((c - start) / (end - start), 0, 1) : 0;
      const width = a.width + (b.width - a.width) * t;
      return { left: c - width / 2, width, top: a.top + (b.top - a.top) * t, height: a.height + (b.height - a.height) * t };
    }
    autoScroll(dt) {
      const drag = this.drag;
      if (!drag?.moved) return false;
      const bounds = this.viewport;
      const direction = drag.clientX > bounds.right - 4 ? 1 : drag.clientX < bounds.left + 4 ? -1 : 0;
      if (!direction) return false;
      const before = this.scroller.scrollLeft;
      this.scroller.scrollLeft += direction * 180 * dt;
      if (Math.abs(this.scroller.scrollLeft - before) < 0.1) return false;
      drag.wanted = drag.offset + drag.clientX - drag.left + this.scroller.scrollLeft - drag.scroll;
      this.place(this.rectAt(drag.wanted), 'drag');
      return true;
    }
    pointerUp = event => this.finish(event, false);
    pointerCancel = event => {
      // Touch implicitly captures the button first; transferring capture to the
      // control emits a bubbling lostpointercapture from that button.
      if (event.type === 'lostpointercapture' && event.target !== this) return;
      this.finish(event, true);
    };
    finish(event, cancelled) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null; this.state.press.target = this.state.lift.target = 0;
      this.removeAttribute('data-glass-dragging');
      try { if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId); } catch {}
      if (drag.moved) {
        this.suppressUntil = performance.now() + 400;
        let selected = drag.origin;
        if (!cancelled) {
          let distance = Infinity;
          this.rects.forEach((r, i) => {
            const d = Math.abs(r.left + r.width / 2 - drag.wanted);
            if (d < distance) { distance = d; selected = this.rects[i].element; }
          });
        }
        if (selected) {
          this.place(this.geometry(selected), 'animate');
          if (!cancelled && selected !== this.selected()) {
            this.committing = true; selected.click(); this.committing = false;
          }
        }
      }
      this.wake();
    }
    click = event => {
      if (!this.committing && performance.now() < (this.suppressUntil || 0)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      const pill = event.target.closest('.yumi-pill-nav__pill');
      if (!pill || !this.contains(pill) || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.button > 0) return;
      if (pill.tagName === 'A' && pill.target !== '_blank' && !pill.hasAttribute('download')) {
        const url = new URL(pill.href);
        if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search)) return;
        event.preventDefault();
        this.navigation = url.href;
        this.place(this.geometry(pill), 'animate'); this.wake();
      }
    };
    keyDown = event => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const pills = this.pills(), index = pills.indexOf(event.target);
      if (index < 0) return;
      this.suppressUntil = 0;
      let next;
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + pills.length) % pills.length;
      else if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + pills.length) % pills.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = pills.length - 1;
      else return;
      event.preventDefault(); pills[next].focus({ preventScroll: true });
      pills[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      pills[next].click();
    };
  });
})();

